import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, Clock, Truck, CheckCircle, XCircle } from 'lucide-react';
import { orderAPI } from '../api';
import { useAuth } from '../contexts/AuthContext';

const statusConfig = {
  pending: { icon: Clock, color: 'text-amber-600 bg-amber-50', label: 'Pending' },
  processing: { icon: Package, color: 'text-blue-600 bg-blue-50', label: 'Processing' },
  shipped: { icon: Truck, color: 'text-purple-600 bg-purple-50', label: 'Shipped' },
  delivered: { icon: CheckCircle, color: 'text-green-600 bg-green-50', label: 'Delivered' },
  cancelled: { icon: XCircle, color: 'text-red-600 bg-red-50', label: 'Cancelled' },
};

export default function OrdersPage() {
  const { user, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      orderAPI.getOrders().then(r => setOrders(r.data)).catch(() => {}).finally(() => setLoading(false));
    }
  }, [user]);

  if (authLoading) return <div className="max-w-3xl mx-auto px-4 py-12"><div className="animate-pulse space-y-4">{[1,2].map(i => <div key={i} className="h-32 bg-gray-200 rounded" />)}</div></div>;

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <Package size={48} className="mx-auto text-gray-300 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Chivo', sans-serif" }}>Sign in to view orders</h2>
        <Link to="/login" className="bg-[#F97316] text-black font-bold py-3 px-8 rounded inline-block" data-testid="orders-login-btn">Sign In</Link>
      </div>
    );
  }

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-12"><div className="animate-pulse space-y-4">{[1,2].map(i => <div key={i} className="h-32 bg-gray-200 rounded" />)}</div></div>;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="orders-page">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight mb-6" style={{ fontFamily: "'Chivo', sans-serif" }}>My Orders</h1>

      {orders.length === 0 ? (
        <div className="text-center py-16 bg-white border rounded" data-testid="no-orders">
          <Package size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-bold text-gray-900 mb-2">No orders yet</h3>
          <p className="text-gray-500 text-sm mb-6">Start shopping to see your order history here.</p>
          <Link to="/products" className="bg-[#F97316] text-black font-bold py-3 px-8 rounded inline-block">Browse Products</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map(order => {
            const st = statusConfig[order.status] || statusConfig.pending;
            const StatusIcon = st.icon;
            return (
              <div key={order.id} className="bg-white border rounded overflow-hidden" data-testid={`order-${order.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-4 bg-gray-50 border-b">
                  <div className="flex gap-8 text-sm">
                    <div><p className="text-gray-500 text-xs">Order ID</p><p className="font-mono font-semibold text-gray-900">{order.id.slice(0, 8)}...</p></div>
                    <div><p className="text-gray-500 text-xs">Date</p><p className="font-medium text-gray-900">{new Date(order.created_at).toLocaleDateString()}</p></div>
                    <div><p className="text-gray-500 text-xs">Total</p><p className="font-bold text-[#0F766E]">${order.total?.toFixed(2)}</p></div>
                  </div>
                  <span className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full ${st.color}`}>
                    <StatusIcon size={14} /> {st.label}
                  </span>
                </div>
                <div className="px-6 py-4">
                  <div className="space-y-2">
                    {order.items?.map((item, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm">
                        {item.image_url && <img src={item.image_url} alt="" className="w-10 h-10 rounded object-cover" />}
                        <span className="flex-1 text-gray-700">{item.product_name}</span>
                        <span className="text-gray-500">x{item.quantity}</span>
                        <span className="font-semibold">${item.item_total?.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  {order.shipping_address && (
                    <p className="text-xs text-gray-500 mt-3 pt-3 border-t">Ships to: {order.shipping_address}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
